import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "@tanstack/react-router";
import { AnimatePresence, motion } from "framer-motion";
import { Users, Utensils } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "~/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { RadioGroup, RadioGroupItem } from "~/components/ui/radio-group";
import { Switch } from "~/components/ui/switch";
import { DIETARY_OPTIONS, type DietaryPreference } from "~/lib/api/types";
import { useRsvp } from "~/lib/hooks/useRsvp";
import { cn } from "~/lib/utils";

type RsvpModalProps = {
	eventId: string;
	open: boolean;
	onClose: () => void;
	allowPlusOne: boolean;
	collectDietaryPrefs: boolean;
};

const MotionFields = ({
	children,
}: {
	children: React.ReactNode;
}) => (
	<motion.div
		initial={{ height: 0, opacity: 0 }}
		animate={{ height: "auto", opacity: 1 }}
		exit={{ height: 0, opacity: 0 }}
		transition={{ duration: 0.2 }}
		className="overflow-hidden"
	>
		{children}
	</motion.div>
);

export function RsvpModal({
	eventId,
	open,
	onClose,
	allowPlusOne,
	collectDietaryPrefs,
}: RsvpModalProps) {
	// Create a base schema that can be adapted at runtime
	const baseSchema = z.object({
		bringingPlusOne: z.boolean().default(allowPlusOne),
		plusOneName: z.string().optional(),
		dietaryPreference: z.enum(DIETARY_OPTIONS).optional(),
		plusOneDietaryPreference: z.enum(DIETARY_OPTIONS).optional(),
	});

	// Create validation refinements based on props
	const rsvpSchema = baseSchema.superRefine((data, ctx) => {
		// Validate plusOneName if bringing a plus one and plus one is allowed
		if (
			allowPlusOne &&
			data.bringingPlusOne &&
			(!data.plusOneName || data.plusOneName.length < 2)
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "Guest name must be at least 2 characters",
				path: ["plusOneName"],
			});
		}

		// Enforce dietary preference if it's being collected
		if (collectDietaryPrefs && !data.dietaryPreference) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "Please select your dietary preference",
				path: ["dietaryPreference"],
			});
		}

		// Enforce plus one dietary preference if all conditions are met
		if (
			collectDietaryPrefs &&
			allowPlusOne &&
			data.bringingPlusOne &&
			!data.plusOneDietaryPreference
		) {
			ctx.addIssue({
				code: z.ZodIssueCode.custom,
				message: "Please select your guest's dietary preference",
				path: ["plusOneDietaryPreference"],
			});
		}
	});

	type RsvpFormData = z.infer<typeof rsvpSchema>;

	const { submitRsvp, isSubmitting } = useRsvp(eventId);
	const router = useRouter();
	const {
		register,
		handleSubmit,
		watch,
		setValue,
		reset,
		clearErrors,
		setError,
		formState: { errors },
	} = useForm<RsvpFormData>({
		resolver: zodResolver(rsvpSchema),
		defaultValues: {
			bringingPlusOne: allowPlusOne,
			plusOneName: "",
			dietaryPreference: undefined,
			plusOneDietaryPreference: undefined,
		},
		mode: "onSubmit",
		reValidateMode: "onChange",
	});

	// Reset the form when props change
	useEffect(() => {
		reset({
			bringingPlusOne: allowPlusOne,
			plusOneName: "",
			dietaryPreference: undefined,
			plusOneDietaryPreference: undefined,
		});
	}, [allowPlusOne, reset]);

	const bringingPlusOne = watch("bringingPlusOne");
	const dietaryPreference = watch("dietaryPreference");
	const plusOneDietaryPreference = watch("plusOneDietaryPreference");

	const handlePlusOneChange = (checked: boolean) => {
		setValue("bringingPlusOne", checked);
		if (!checked) {
			setValue("plusOneName", "");
			clearErrors("plusOneName");
		}
	};

	const onSubmit = async (data: RsvpFormData) => {
		await submitRsvp({
			status: "accepted",
			bringingPlusOne: data.bringingPlusOne,
			plusOneName: data.bringingPlusOne ? data.plusOneName : undefined,
			dietaryPreference: collectDietaryPrefs
				? data.dietaryPreference
				: undefined,
			plusOneDietaryPreference:
				collectDietaryPrefs && allowPlusOne && data.bringingPlusOne
					? data.plusOneDietaryPreference
					: undefined,
		});

		router.invalidate();

		onClose();
	};

	return (
		<Dialog open={open} onOpenChange={onClose}>
			<DialogContent className="bg-black/90 backdrop-blur-xl border-white/10 text-white sm:max-w-md">
				<div className="flex items-center justify-between pb-4">
					<DialogTitle className="text-2xl sm:text-3xl font-bold bg-gradient-to-br from-white via-white/90 to-white/80 bg-clip-text text-transparent">
						Join the Event
					</DialogTitle>
				</div>

				<form onSubmit={handleSubmit(onSubmit)} className="space-y-6 py-4">
					{/* Plus One Section */}
					{allowPlusOne && (
						<div className="space-y-6">
							<div className="flex items-start gap-4">
								<div className="shrink-0 p-3 rounded-xl bg-accent/10">
									<Users className="w-6 h-6 text-accent" />
								</div>
								<div className="flex-1">
									<div className="flex items-center justify-between">
										<div>
											<h3 className="text-lg font-medium text-white">
												Bringing someone?
											</h3>
											<p className="text-white/60 mt-1">
												You can bring a plus one
											</p>
										</div>
										<Switch
											id="plus-one"
											checked={bringingPlusOne}
											onCheckedChange={handlePlusOneChange}
											className={cn(
												"data-[state=checked]:bg-accent",
												"data-[state=unchecked]:bg-white/10",
											)}
										/>
									</div>

									<AnimatePresence initial={false}>
										{bringingPlusOne && (
											<MotionFields>
												<div className="mt-6 space-y-3">
													<Label
														htmlFor="plus-one-name"
														className="text-white/80"
													>
														Guest's Name
													</Label>
													<Input
														id="plus-one-name"
														{...register("plusOneName", {
															onChange: (e) => {
																if (e.target.value) {
																	clearErrors("plusOneName");
																}
															},
														})}
														placeholder="Enter your guest's name"
														className={cn(
															"h-12 bg-black/20 border-white/10 text-white placeholder:text-white/40",
															"focus:border-accent/50 focus:ring-accent/50",
															errors.plusOneName &&
																"border-red-500/50 focus:border-red-500/50 focus:ring-red-500/20",
														)}
													/>
													{errors.plusOneName && (
														<p className="text-red-400 text-sm">
															{errors.plusOneName.message}
														</p>
													)}
												</div>
											</MotionFields>
										)}
									</AnimatePresence>
								</div>
							</div>
						</div>
					)}

					{collectDietaryPrefs && (
						<div className="space-y-6">
							<div className="flex items-start gap-4">
								<div className="shrink-0 p-3 rounded-xl bg-accent/10">
									<Utensils className="w-6 h-6 text-accent" />
								</div>
								<div className="flex-1">
									<div>
										<h3 className="text-lg font-medium text-white">
											Dietary Preferences
										</h3>
										<p className="text-white/60 mt-1 mb-4">
											Let us know your food preferences
										</p>

										<div className="space-y-5">
											<div>
												<Label className="text-white/80 mb-2 block">
													Your preference
												</Label>
												<RadioGroup
													value={dietaryPreference}
													onValueChange={(value: DietaryPreference) => {
														setValue("dietaryPreference", value);
														setError("dietaryPreference", {
															message: undefined,
														});
													}}
													className="flex flex-col gap-2"
												>
													{DIETARY_OPTIONS.map((option) => (
														<div
															key={option}
															className="flex items-center gap-2"
														>
															<RadioGroupItem
																value={option}
																id={`pref-${option}`}
																className="border-white/20 text-white"
															/>
															<Label
																htmlFor={`pref-${option}`}
																className="text-white cursor-pointer"
															>
																{option === "vegetarian"
																	? "Vegetarian"
																	: "Non-vegetarian"}
															</Label>
														</div>
													))}
												</RadioGroup>
												{errors.dietaryPreference && (
													<p className="text-red-400 text-sm mt-1">
														{errors.dietaryPreference.message}
													</p>
												)}
											</div>

											<AnimatePresence initial={false}>
												{allowPlusOne && bringingPlusOne && (
													<MotionFields>
														<div>
															<Label className="text-white/80 mb-2 block">
																Your guest's preference
															</Label>
															<RadioGroup
																value={plusOneDietaryPreference}
																onValueChange={(value: DietaryPreference) => {
																	setValue("plusOneDietaryPreference", value);
																	setError("plusOneDietaryPreference", {
																		message: undefined,
																	});
																}}
																className="flex flex-col gap-2"
															>
																{DIETARY_OPTIONS.map((option) => (
																	<div
																		key={`plusone-${option}`}
																		className="flex items-center gap-2"
																	>
																		<RadioGroupItem
																			value={option}
																			id={`plusone-${option}`}
																			className="border-white/20 text-white"
																		/>
																		<Label
																			htmlFor={`plusone-${option}`}
																			className="text-white cursor-pointer"
																		>
																			{option === "vegetarian"
																				? "Vegetarian"
																				: "Non-vegetarian"}
																		</Label>
																	</div>
																))}
															</RadioGroup>
															{errors.plusOneDietaryPreference && (
																<p className="text-red-400 text-sm mt-1">
																	{errors.plusOneDietaryPreference.message}
																</p>
															)}
														</div>
													</MotionFields>
												)}
											</AnimatePresence>
										</div>
									</div>
								</div>
							</div>
						</div>
					)}

					{/* Action Buttons */}
					<div className="flex justify-end gap-3">
						<Button
							type="button"
							variant="ghost"
							onClick={onClose}
							className="text-white/60 hover:text-white hover:bg-white/5"
						>
							Cancel
						</Button>
						<Button
							type="submit"
							disabled={isSubmitting}
							className={cn(
								"bg-accent hover:bg-accent-light text-accent-foreground",
								"transition-all duration-300",
								"hover:scale-[1.02] active:scale-[0.98]",
								"shadow-lg shadow-accent/25",
								"px-8",
							)}
						>
							{isSubmitting ? (
								<div className="flex items-center gap-2">
									<div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
									<span>Responding...</span>
								</div>
							) : (
								"Count me in!"
							)}
						</Button>
					</div>
				</form>
			</DialogContent>
		</Dialog>
	);
}
